import {createAsyncThunk} from '@reduxjs/toolkit';
import type {DesignSelection, PostSortingMetric} from '@Components/social-media/social-media.types';
import type {MediaTypePublishingParams, PlatformMediaType, SocialMediaAccountId, SocialMediaAccountIdString} from '@Components/social-media/post.vo';
import type {SocialMediaPublishingParams} from '@Components/social-media/publish-params.types';
import type {SocialMediaAccountsResponse, SocialMediaPostsResponse} from './social-media-extra-reducers';

export interface SocialMediaGridRequestThunkParams {
  offset: number;
  limit: number;
  getDeleted: boolean;
  sortPostsBy: PostSortingMetric;
  getSharedInTeam: boolean;
}

export interface AccountSelectionPreferencePayload {
  socialMediaAccountIds: SocialMediaAccountId[];
  idPost: string | undefined;
}

export interface PublishForAccountParams {
  idPost: string | null;
  idSocialMediaAccount: SocialMediaAccountId;
  isRetry: boolean;
  mediaType: PlatformMediaType;
}

export interface SocialMediaPublishingParamsUpdateParams {
  idPost: string | null;
  designs: DesignSelection[] | null;
  publishOn: number | null;
  timeZoneOffset: number | null;
  publishingParams: Record<SocialMediaAccountIdString, MediaTypePublishingParams>;
}

export const getSocialMediaPostsForUser = createAsyncThunk<SocialMediaPostsResponse, SocialMediaGridRequestThunkParams>(
  'getSocialMediaPostsForUser',
  async ({offset, limit, getDeleted, sortPostsBy, getSharedInTeam}): Promise<SocialMediaPostsResponse> => {
    return window.PMW.readLocal('socialmedia/getSocialMediaPostsForUser', {
      offset,
      limit,
      getDeleted,
      sortPostsBy,
      getSharedInTeam,
    });
  }
);

export const getPostableSocialMediaAccountsForUser = createAsyncThunk<SocialMediaAccountsResponse>(
  'getPostableSocialMediaAccountsForUser',
  async (): Promise<SocialMediaAccountsResponse> => {
    return window.PMW.readLocal('socialmedia/getPostableSocialMediaAccountsForUser');
  }
);

export const savePreferredProfileSelectionUser = createAsyncThunk('savePreferredProfileSelectionUser', async (data: AccountSelectionPreferencePayload) => {
  return window.PMW.writeLocal('socialmedia/savePreferredProfileSelectionUser', {
    socialMediaAccountIds: data.socialMediaAccountIds,
    idPost: data.idPost,
  });
});

export const doPublishForAccount = createAsyncThunk('doPublishPostOrReelForAccount', async (data: PublishForAccountParams) => {
  return window.PMW.pollingAjaxCallAsync(
    () => {
      return window.PMW.writeLocal(`socialmedia/doPublishForAccount`, {
        idPost: data.idPost,
        idSocialMediaAccount: data.idSocialMediaAccount,
        isRetry: data.isRetry,
        mediaType: data.mediaType,
      });
    },
    () => {
      return window.PMW.writeLocal('socialmedia/isPostPublished', {
        idPost: data.idPost,
        idSocialMediaAccount: data.idSocialMediaAccount,
        mediaType: data.mediaType,
      });
    }
  );
});

const checkGraphicsMetaInPublishingParams = (publishingParams: SocialMediaPublishingParams, data: SocialMediaPublishingParamsUpdateParams): void => {
  const {graphicsMetaMap} = publishingParams;

  Object.values(graphicsMetaMap).forEach((pageGraphicMeta) => {
    Object.values(pageGraphicMeta).forEach((graphicMetaData) => {
      if (graphicMetaData.isVideo === undefined) {
        window.PMW.log(`isVideo missing in graphicsMeta for post, ${data.idPost}`);
        return;
      }
    });
  });
};

const throwErrorForIncorrectGraphicsMeta = (data: SocialMediaPublishingParamsUpdateParams): void => {
  const mediaTypeParams = data.publishingParams;

  for (const key in mediaTypeParams) {
    const publishingParams = mediaTypeParams[key];

    const storyParams = publishingParams.STORIES;
    const postParams = publishingParams.POST;
    const reelParams = publishingParams.REELS;

    if (postParams) {
      checkGraphicsMetaInPublishingParams(postParams, data);
    }

    if (storyParams) {
      checkGraphicsMetaInPublishingParams(storyParams, data);
    }

    if (reelParams) {
      checkGraphicsMetaInPublishingParams(reelParams, data);
    }
  }
};

export const updateSocialMediaPostPublishingParams = createAsyncThunk('updateSocialMediaPostPublishingParams', async (data: SocialMediaPublishingParamsUpdateParams) => {
  throwErrorForIncorrectGraphicsMeta(data);
  return window.PMW.writeLocal('socialmedia/updateSocialMediaPostPublishingParams', {
    idPost: data.idPost,
    designsInfo: JSON.stringify(data.designs),
    publishOn: data.publishOn,
    timeZoneOffset: data.timeZoneOffset,
    publishingParams: JSON.stringify(data.publishingParams),
  });
});

export const getSocialMediaAccountsForUser = createAsyncThunk<SocialMediaAccountsResponse>('getSocialMediaAccountsForUser', async (): Promise<SocialMediaAccountsResponse> => {
  return window.PMW.readLocal('socialmedia/getSocialMediaAccountsForUser');
});
